import { Injectable } from '@angular/core';
import { HttpClient, HttpResponse } from '@angular/common/http';
import { FormGroup } from '@angular/forms';
import { Platform } from '@angular/cdk/platform';

import { throwError, Observable } from 'rxjs';
import { tap, map, catchError } from 'rxjs/operators';

import { ChannelsEnum } from 'src/app/shared/components/lead-form/lead-form.component';
import { EnvironmentService } from '../../enviroment/environment.service';
import { GoogleTagManagerService } from '../../analytics/google-tag-manager.service';
import { Channels } from '../../models/channels';
import { Profile } from '../../models/profile';

interface LeadResponse {
  taskId: string;
}

@Injectable({ providedIn: 'root' })
export class LeadFormService {

  url = 'lead';
  private DEFAULT_VALUE = '1';

  constructor(
    public readonly http: HttpClient,
    public readonly envService: EnvironmentService,
    public platform: Platform,
    public readonly googleTagService: GoogleTagManagerService
  ) { }

  save(lead): Observable<HttpResponse<LeadResponse>> {
    return this.http.post<any>(`${this.envService.getBaseLeadUrl()}${this.url}`, lead)
      .pipe(
        map(response => response.taskId || this.generateLeadId()),
        tap((leadId) => this.sendLeadEventForPurchase(lead, leadId)),
        catchError((err) => {
          this.googleTagService.pushTag({
            event: 'event',
            eventCategory: 'lopes:ecommerce:fale-com-o-corretor:site',
            eventAction: 'erro:fale-corretor',
            eventLabel: 'erro-de-servidor',
          });
          return throwError(err);
        })
      );
  }

  buildLead(
    channel: number, companyId: string, form: FormGroup, nickname: string
  ) {
    return {
      type_id: 1,
      contact_type_id: channel,
      chatChannelId: '',
      reference: '',
      end_point: '1',
      company_id: companyId,
      nickname,
      bm_utm: '',
      utm: '',
      user: {
        name: form.get('name').value,
        email: form.get('email').value,
        phone: '55' + (form.get('telefone').value)
      },
      lps_media_origin: ''
    };
  }

  startCommunicationForWhatsApp(
    channel: ChannelsEnum, brokerPhone: string
  ) {
    if (channel === ChannelsEnum.WHATSAPP) {
      this.callWhatsApp(brokerPhone);
    }
  }

  callWhatsApp(phone: string) {
    const msg = `?text=Olá, gostaria de mais informações`;
    if (!this.platform.ANDROID && !this.platform.IOS) {
      window.open(`https://web.whatsapp.com/send${msg}&phone=${phone}`, '_blank');
    } else {
      const target = this.platform.SAFARI ? '_top' : '_blank';
      window.open(`https://wa.me/${phone}${msg}`, target);
    }
  }

  sendLeadEventForPurchase(lead, leadId: string) {
    const ecommerce = {
      purchase: {
        actionField: {
          id: leadId,
          revenue: this.DEFAULT_VALUE,
          affiliation: 'fale-com-o-corretor'
        }
      }
    };
    const leadEvent = this.eventFor(
      'purchase',
      'lopes:ecommerce:fale-com-o-corretor:site',
      'purchase',
      ecommerce,
      lead.contact_type_id
    );
    this.googleTagService.pushTag(leadEvent);
  }

  sendLeadEventForNameChanged() {
    const leadEvent = this.eventFor(
      'addToCart',
      'lopes:ecommerce:fale-com-o-corretor:site',
      'addToCart');

    this.googleTagService.pushTag(leadEvent);
  }

  sendLeadEventForCheckout(chosenCommunication: string) {
    const ecommerce = {
      checkout: {
        actionField: {
          step: '1'
        }
      }
    };
    const leadEvent = this.eventFor(
      'checkout',
      'lopes:ecommerce:fale-com-o-corretor:site',
      'checkout',
      ecommerce,
      null,
      'checkout-etapa-1');

    leadEvent.dimension6 = chosenCommunication;
    this.googleTagService.pushTag(leadEvent);
  }

  private eventFor(
    eventType: string,
    eventCategory: string,
    eventAction: string,
    ecommerce?: object,
    contactTypeId?: number,
    eventLabel?: string
  ) {
    return {
      event: eventType,
      eventCategory,
      eventAction,
      eventLabel,
      ecommerce,
      dimension6: contactTypeId && new Channels().channelById(contactTypeId.toString())
    };
  }

  generateLeadId() {
    return Math.random().toString(36).substring(2) + Date.now().toString(36);
  }

  checkNameChanged(name: string, oldName: string): void {
    if ( this.isNameFilled(name) && this.nameHasChanged(name, oldName)) {
      this.sendLeadEventForNameChanged();
    }
  }

  nameHasChanged(name: string, oldName: string) {
    return name !== oldName;
  }

  isNameFilled(name: string) {
    return name.trim().length !== 0;
  }
}
